// Registration is split by account level: an organization gets the platform in
// full, a business account only the customer feedback slice of it (see
// base/services/accountLevel.service.js). The Customer Feedback plan is the one
// a retail, food or events account subscribes to; every other plan is an
// organization's.
//
// The level is set when the subscription is created, not at /Account/register,
// so the package is picked after the account exists. POST /Payment/subscriptions
// takes no level: the server sets it from the packet (Customer Feedback is a
// business account), and again when the plan is changed to the other kind's
// (POST /Payment/change-plan). The app reads it back from the sign in, and after
// a plan is picked or changed (components/Billing/billingSignIn.js,
// rememberNewSubscription).

import { OrganizationLevel } from "../../../base/services/accountLevel.service";

export const AccountLevel = {
   Organization: "organization",
   Business: "business"
};

// What each track is called and who it is for, in the order the package step
// lays them out: retail on the left, organizations on the right.
export const ACCOUNT_LEVELS = [
   {
      key: AccountLevel.Business,
      emoji: "🛍️",
      name: "Retail, Food & Events",
      tagline: "For the places your customers walk into",
      description:
         "Shops, cafés, restaurants and event venues collecting feedback from their customers and guests.",
      audience: ["Retail", "Food & drink", "Events"],
      // plansLabel: "Your plan",
      // No trial length here: each packet reports its own (`trialDays`), and
      // the cards print that — see formatTrialDays in planPricing.js.
      // What this level's plan is worth saying, because the numbers an
      // organization's plan is sold on are not it: a café is one place
      // collecting feedback, not a team with a workspace of members and a
      // database behind it, so a seat count and a storage figure tell it
      // nothing it can act on. These are the platform it does open — see
      // base/services/accountLevel.service.js, which is what actually grants
      // them.
      planFeatures: [
         "Customer feedback surveys",
         // "Feedback pages to share and preview",
         "Analytics on the responses you collect"
      ]
   },
   {
      key: AccountLevel.Organization,
      emoji: "🏢",
      name: "Organizations & Companies",
      tagline: "For teams running research and programmes",
      description:
         "NGOs, institutions and companies running surveys, projects and dashboards across their workspaces.",
      audience: ["NGOs", "Institutions", "Companies"],
      // plansLabel: "Plans",
      // An organization's plan is sold on its limits, which the packet carries
      // itself, so there is nothing to add to them here.
      planFeatures: []
   }
];

/** The copy one account level is presented with, looked up by its key. */
export const getAccountLevelCopy = (accountLevel) =>
   ACCOUNT_LEVELS.find((level) => level.key === accountLevel);

/**
 * The level the register endpoint records the new organization under, which is
 * what tells the platform apart afterwards (see
 * base/services/accountLevel.service.js). Anything that isn't a retail, food or
 * events account registers as an organization.
 */
export const getOrganizationLevel = (accountLevel) =>
   accountLevel === AccountLevel.Business
      ? OrganizationLevel.Business
      : OrganizationLevel.Organization;

/** The account level a registered organization's numeric level stands for. */
export const getAccountLevel = (organizationLevel) =>
   organizationLevel === OrganizationLevel.Business
      ? AccountLevel.Business
      : AccountLevel.Organization;

/** The plan a business account subscribes to. Matched on the packet's name. */
const CUSTOMER_FEEDBACK_PLAN = "Customer Feedback";

const isCustomerFeedbackPlan = (plan) =>
   plan?.name?.trim().toLowerCase() === CUSTOMER_FEEDBACK_PLAN.toLowerCase();

/**
 * The plans one level subscribes to: a business account the Customer Feedback
 * plan, an organization the general ones. A list asked for without a level is
 * left untouched.
 */
export const filterPlansByAccountLevel = (plans, accountLevel) => {
   const options = plans ?? [];

   if (accountLevel === AccountLevel.Business) {
      return options.filter(isCustomerFeedbackPlan);
   }

   if (accountLevel === AccountLevel.Organization) {
      return options.filter((plan) => !isCustomerFeedbackPlan(plan));
   }

   return options;
};

/** The account level a plan gives the organization subscribed to it. */
export const getPlanAccountLevel = (plan) =>
   isCustomerFeedbackPlan(plan) ? AccountLevel.Business : AccountLevel.Organization;

/**
 * The plans an account can change to on the subscription page: every plan, of
 * either level, whichever level the account is at now. The server sets the
 * level from the plan, so a move across changes what the account can open.
 * The account's own plan is always among them, even while its level lags
 * behind its plan.
 *
 * Dearest first, as the page lays plans out: it shows them in reverse. The
 * organization plans in the order the API gives them, then Customer Feedback.
 */
export const getPlanChangeOptions = (plans) => [
   ...filterPlansByAccountLevel(plans, AccountLevel.Organization),
   ...filterPlansByAccountLevel(plans, AccountLevel.Business)
];
